import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { CircleHelp, Loader2, X } from 'lucide-react';
import { AuthGuard } from '../../app/AuthGuard';
import type { ScreenId } from '../../app/screenRoutes';
import { navigateToLandingSection } from '../../config/navigation';
import { useFigmaBreakpoint, type FigmaBreakpoint } from '../../figma/useFigmaBreakpoint';
import {
  carrierCabinetApi,
  type CarrierProfileDto,
} from '../../features/carrierCabinet/api';
import {
  deleteAsset,
  getCarrierDocuments,
  type CarrierDocument,
  type DocumentType,
} from '../../features/carrierVerification/api';
import { useAuth } from '../../state/auth';
import { useLanguage } from '../../i18n/LanguageProvider';
import {
  AccountActions,
  Button,
  CabinetHelpLink,
  Footer,
  Header,
  MessageIcon,
  MobileDrawer,
  NotificationBell,
  PhotoCard,
  ReviewCard,
  SliderButton,
  Stars,
  Tag,
  VideoCard,
  type MobileDrawerAction,
  type StarsRating,
} from '../../ui';
import { CarrierCabinetLayout } from '../../layouts/CarrierCabinet/CarrierCabinetLayout';
import { ProfilePhoneField } from '../../ui/ProfilePhoneField/ProfilePhoneField';
import './carrier-profile.css';
import './carrier-profile-responsive.css';

export type CarrierProfileScreenId = Extract<
  ScreenId,
  'carrier-profile-internal' | 'carrier-profile-external'
>;

interface PublicFigmaLayout {
  root: string;
  alert: string;
  main: string;
  hero: string;
  aboutVehicle: string;
  trips: string;
  portfolio: string;
  reviews: string;
  back: string;
}

const PUBLIC_FIGMA: Record<FigmaBreakpoint, PublicFigmaLayout> = {
  '1920': {
    root: '106:32475', alert: '106:32479', main: '137:34675', hero: '106:32495',
    aboutVehicle: '106:32543', trips: '106:32566', portfolio: '106:32596', reviews: '106:32627', back: '106:32674',
  },
  '1440': {
    root: '156:38193', alert: '156:38195', main: '156:38198', hero: '156:38199',
    aboutVehicle: '156:38244', trips: '156:38267', portfolio: '156:38297', reviews: '156:38319', back: '156:38366',
  },
  '768': {
    root: '160:39220', alert: '160:39222', main: '160:39225', hero: '160:39226',
    aboutVehicle: '160:39271', trips: '160:39294', portfolio: '160:39324', reviews: '160:39346', back: '160:39393',
  },
  '375': {
    root: '164:40467', alert: '164:40469', main: '164:40472', hero: '164:40473',
    aboutVehicle: '164:40518', trips: '164:40541', portfolio: '164:40571', reviews: '164:40593', back: '164:40639',
  },
};

const PUBLIC_LOW_PHOTO_FIGMA: PublicFigmaLayout = {
  root: '2525:26891',
  alert: '2525:26893',
  main: '2525:26896',
  hero: '2525:26897',
  aboutVehicle: '2525:26942',
  trips: '2525:26965',
  portfolio: '2525:26987',
  reviews: '2525:27009',
  back: '2525:27039',
};

const CARRIER_TYPE_LABEL: Record<CarrierProfileDto['carrier_type'], string> = {
  person: 'Частное лицо',
  company: 'Компания',
};

const SERVICE_TYPE_LABEL: Record<CarrierProfileDto['service_type'], string> = {
  auto: 'Автоперевозки',
  air_courier: 'Авиакурьер',
  cargo_broker: 'Карго-агент',
  airport_meet: 'Встреча питомца',
};

const VERIFICATION_LABEL: Record<string, string> = {
  approved: 'Подтверждён',
  draft: 'Черновик',
  pending: 'На проверке',
  needs_changes: 'Требуются исправления',
  rejected: 'Отклонён',
  expired: 'Истёк срок документов',
};

const DOCUMENT_STATUS_LABEL: Record<CarrierDocument['status'] | 'missing', string> = {
  approved: 'Подтверждён',
  draft: 'Черновик',
  pending: 'На проверке',
  rejected: 'Отклонён',
  missing: 'Не загружен',
};

const RECENT_TRIP_ICON: Record<CarrierProfileDto['recent_trips'][number]['pet_type'], string> = {
  dog: '/figma/assets/pet-trip-dog.svg',
  cat: '/figma/assets/pet-trip-cat.svg',
  rodent: '/figma/assets/pet-trip-rodent.svg',
  bird: '/figma/assets/pet-trip-bird.svg',
  farm: '/figma/assets/pet-trip-farm.svg',
  other: '/figma/assets/pet-trip-other.svg',
};

const MONTHS = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

function imagePreviewUrl(url: string, size: number) {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}preview=${size}`;
}

function navigate(screen: string, params?: Record<string, string | number>) {
  if (screen === 'landing') {
    if (window.location.pathname.startsWith('/portal')) {
      window.location.href = '/#landing';
      return;
    }
  }
  const query = params
    ? `?${new URLSearchParams(Object.entries(params).map(([key, value]) => [key, String(value)])).toString()}`
    : '';
  window.location.hash = `${screen}${query}`;
}

function hashNumber(name: string): number | null {
  const queryIndex = window.location.hash.indexOf('?');
  if (queryIndex < 0) return null;
  const value = Number(new URLSearchParams(window.location.hash.slice(queryIndex + 1)).get(name));
  return Number.isFinite(value) && value > 0 ? value : null;
}

function memberSince(value: string, language: 'ru' | 'en'): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  if (language === 'en') {
    return `Carrier since ${new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(date)}`;
  }
  return `Перевозчик с ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

function shortDate(value: string, language: 'ru' | 'en'): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })
    .format(date)
    .replace(' г.', '');
}

function recentTripDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${day}.${month}.${year}`;
}

function initials(value: string): string {
  return value
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();
}

function maskedPhone(value: string | null | undefined): string {
  const digits = (value ?? '').replace(/\D/g, '');
  if (digits.length < 11) return value ?? '';
  const normalized = digits.slice(-11);
  return `+${normalized[0]} ${normalized.slice(1, 4)} ***-**-${normalized.slice(-2)}`;
}

function starsRating(value: string | number): StarsRating | null {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return null;
  return Math.min(5, Math.max(0.5, Math.round(number * 2) / 2)) as StarsRating;
}

function splitFeatures(value: string): string[] {
  return value.split(/[,;\n]+/).map((item) => item.trim()).filter(Boolean);
}

function primaryVehicle(profile: CarrierProfileDto) {
  return profile.vehicles.find((vehicle) => vehicle.is_primary) ?? profile.vehicles[0] ?? null;
}

function videoReviewTitle(asset: CarrierProfileDto['reviews'][number]['photos'][number], review: CarrierProfileDto['reviews'][number]) {
  const filename = asset.original_filename.replace(/\.[^.]+$/, '').trim();
  return filename && !/^video-review-\d+$/i.test(filename)
    ? filename
    : `${review.author_name}: ${review.route.from_city} — ${review.route.to_city}`;
}

function basedOnReviewsLabel(count: number, language: 'ru' | 'en') {
  if (language === 'en') return `Based on ${count} ${count === 1 ? 'review' : 'reviews'}`;
  return `На основе ${count} ${count % 10 === 1 && count % 100 !== 11 ? 'отзыва' : 'отзывов'}`;
}

function scrollTrack(ref: RefObject<HTMLElement | null>, direction: -1 | 1) {
  ref.current?.scrollBy({ left: direction * Math.max(280, ref.current.clientWidth * 0.8), behavior: 'smooth' });
}

function useTrackScroll(ref: RefObject<HTMLElement | null>, deps: unknown[] = []) {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = ref.current;
    if (!el) {
      setHasOverflow(false);
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const overflow = el.scrollWidth > el.clientWidth + 4;
    setHasOverflow(overflow);
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, [ref]);

  useEffect(() => {
    updateScrollState();
    const el = ref.current;
    if (!el) return;

    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateScrollState) : null;
    observer?.observe(el);

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
      observer?.disconnect();
    };
  }, [updateScrollState, ...deps]);

  return { canScrollLeft, canScrollRight, hasOverflow, updateScrollState };
}

function ProfileNavigation({
  accountLabel,
  menuOpen,
  onMenuOpen,
}: {
  accountLabel?: string;
  menuOpen: boolean;
  onMenuOpen: (open: boolean) => void;
}) {
  const auth = useAuth();
  const label = accountLabel || auth.user?.display_name || auth.user?.email.split('@')[0] || '';

  function drawerAction(action: MobileDrawerAction) {
    if (action === 'how-it-works') {
      onMenuOpen(false);
      navigateToLandingSection('how');
      return;
    }
    if (action === 'why-lovo' || action === 'reviews') {
      onMenuOpen(false);
      navigateToLandingSection('reviews');
      return;
    }
    const routes: Partial<Record<MobileDrawerAction, string>> = {
      'personal-cabinet': 'carrier-dashboard',
      'carrier-search': 'carrier-search',
      'carrier-offers': 'carrier-my-offers',
      'carrier-active-transports': 'carrier-active-transport',
      'carrier-history': 'carrier-completed',
      'carrier-profile': 'carrier-profile-internal',
      'carrier-billing': 'carrier-earnings',
      'carrier-documents': 'carrier-documents',
      'carrier-settings': 'carrier-settings',
      'carrier-help': 'carrier-help',
      help: 'carrier-help',
      logo: 'landing',
    };
    const route = routes[action];
    if (route) navigate(route);
    onMenuOpen(false);
  }

  return (
    <>
      <Header
        authenticated
        actions={(
          <div className="carrier-cabinet__header-actions" data-figma-node="124:35456">
            <button
              aria-label="Сообщения"
              type="button"
              onClick={() => navigate('carrier-chat-list')}
            >
              <MessageIcon hasUnread />
            </button>
            <button
              aria-label="Уведомления"
              type="button"
              onClick={() => navigate('carrier-notifications')}
            >
              <NotificationBell hasUnread />
            </button>
          </div>
        )}
        account={(
          <button className="carrier-cabinet__account" type="button" onClick={() => navigate('carrier-profile-internal')}>
            <span>{label}</span>
            <img src="/figma/assets/profile-arrow-up-right.svg" alt="" aria-hidden="true" />
          </button>
        )}
        menuExpanded={menuOpen}
        externalDrawer
        onLogoClick={() => navigate('landing')}
        onMenuToggle={() => onMenuOpen(!menuOpen)}
      />
      {menuOpen ? (
        <MobileDrawer
          variant="carrier"
          onClose={() => onMenuOpen(false)}
          onLogoClick={() => navigate('landing')}
          onNavigate={drawerAction}
        />
      ) : null}
    </>
  );
}

function ProfileFooter({ size }: { size: FigmaBreakpoint }) {
  return (
    <Footer
      size={size}
      onNavigate={(action) => {
        if (action === 'logo') navigate('landing');
        if (action === 'customer-cabinet') navigate('my-requests');
        if (action === 'carrier-cabinet') navigate('carrier-dashboard');
        if (action === 'carrier-registration') navigate('carrier-rega-email');
        if (action === 'help') navigate('carrier-help');
      }}
    />
  );
}

function ProfileHero({
  figmaNode,
  internal = false,
  onAvatarUpload,
  onPreview,
  onReviews,
  profile,
  size,
}: {
  figmaNode?: string;
  internal?: boolean;
  onAvatarUpload?: (file: File) => void;
  onPreview?: () => void;
  onReviews: () => void;
  profile: CarrierProfileDto;
  size: FigmaBreakpoint;
}) {
  const { language } = useLanguage();
  const rating = starsRating(profile.review_rating);
  const avatarInput = useRef<HTMLInputElement>(null);
  const vehicle = primaryVehicle(profile);
  const editingHero = figmaNode === '2867:34355';
  const hasAwards = Boolean(profile.awards && profile.awards.length >= 2);
  const badges = hasAwards
    ? profile.awards.slice(0, 2)
    : [SERVICE_TYPE_LABEL[profile.service_type], profile.can_offer ? 'Отклики доступны' : 'Отклики закрыты'];

  const [avatarError, setAvatarError] = useState(false);
  const showAvatar = Boolean(profile.avatar?.url && !avatarError);

  useEffect(() => {
    setAvatarError(false);
  }, [profile.avatar?.id, profile.avatar?.url]);

  function chooseAvatar(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) {
      setAvatarError(false);
      onAvatarUpload?.(file);
    }
    event.target.value = '';
  }

  return (
    <section
      className={`carrier-profile__hero ${internal ? 'carrier-profile__hero--internal' : ''}`}
      data-figma-node={figmaNode ?? (internal ? '2867:35436' : PUBLIC_FIGMA[size].hero)}
    >
      <div className="carrier-profile__cover" data-figma-node={internal ? (editingHero ? '2867:34356' : '2867:35437') : undefined} />
      <div className="carrier-profile__hero-content" data-figma-node={internal ? (editingHero ? '2867:34357' : '2867:35438') : undefined}>
        <div className="carrier-profile__hero-top">
          {onAvatarUpload ? (
            <button
              aria-label="Изменить фото профиля"
              className="carrier-profile__avatar-frame"
              type="button"
              onClick={() => avatarInput.current?.click()}
            >
              {showAvatar ? (
                <img
                  className="carrier-profile__avatar"
                  src={imagePreviewUrl(profile.avatar!.url, 480)}
                  alt={profile.display_name}
                  onError={() => setAvatarError(true)}
                />
              ) : (
                <span className="carrier-profile__avatar-placeholder">
                  <span className="carrier-profile__avatar-placeholder-text">Фото не добавлено</span>
                </span>
              )}
            </button>
          ) : (
            <div className="carrier-profile__avatar-frame">
              {showAvatar ? (
                <img
                  className="carrier-profile__avatar"
                  src={imagePreviewUrl(profile.avatar!.url, 480)}
                  alt={profile.display_name}
                  onError={() => setAvatarError(true)}
                />
              ) : (
                <span className="carrier-profile__avatar-placeholder">
                  <span className="carrier-profile__avatar-placeholder-text">Фото не добавлено</span>
                </span>
              )}
            </div>
          )}
          {onAvatarUpload ? (
            <input ref={avatarInput} hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={chooseAvatar} />
          ) : null}
          {internal && onPreview ? (
            <div className="carrier-profile__avatar-action">
              <Button kind="tertiary" size="medium" icon="up-right" onClick={onPreview}>
                Предпросмотр профиля
              </Button>
            </div>
          ) : null}
        </div>

        <div className="carrier-profile__hero-body">
          <div className="carrier-profile__identity">
            <div className="carrier-profile__name-row">
              <h1 data-i18n-skip>{profile.display_name}</h1>
              <Tag
                type="Verification"
                icon={profile.verification_status === 'approved' ? 'left' : 'no'}
                label={VERIFICATION_LABEL[profile.verification_status] ?? profile.verification_status}
              />
            </div>
            <div className="carrier-profile__occupation">
              <span>{CARRIER_TYPE_LABEL[profile.carrier_type]}</span>
              <i aria-hidden="true" />
              <span>{memberSince(profile.created_at, language)}</span>
            </div>
            <div className="carrier-profile__badges">
              <Tag type="Awards" icon={hasAwards ? 'left' : 'no'} label={badges[0]} />
              <Tag type="Awards" icon={hasAwards ? 'left' : 'no'} label={badges[1]} />
            </div>

            <div className="carrier-profile__rating-section">
              <div className="carrier-profile__rating">
                <strong>{Number(profile.review_rating).toFixed(1)}</strong>
                {rating ? <Stars mode="Light" rating={rating} /> : null}
              </div>
              <Button kind="tertiary" size="medium" onClick={onReviews}>
                {basedOnReviewsLabel(profile.approved_reviews_count ?? profile.reviews_count ?? profile.reviews.length, language)}
              </Button>
            </div>

            <div className="carrier-profile__completed">
              <div><strong>{profile.completed_transports_count}</strong><span>перевозок выполнено</span></div>
              {profile.city?.trim() ? (
                <div className="carrier-profile__location">
                  <img src="/figma/assets/landing-geo.svg" alt="" aria-hidden="true" />
                  <span data-i18n-skip>{profile.city.trim()}</span>
                </div>
              ) : null}
            </div>
          </div>

          <div className="carrier-profile__metrics">
            <div>
              <strong>{profile.verification_rating_percent}%</strong>
              <span>Рейтинг верификации</span>
              <small>Паспорт ・ Лицензия ・ Портфолио</small>
            </div>
            <div>
              <strong>{profile.photo_rating_percent}%</strong>
              <span>Рейтинг фото</span>
              <small>{vehicle ? 'Качество отчётов в пути' : 'Портфолио перевозчика'}</small>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function VehicleContent({ profile }: { profile: CarrierProfileDto }) {
  const vehicle = primaryVehicle(profile);
  const features = splitFeatures(vehicle?.pet_features ?? '');

  return (
    <div className="carrier-profile__vehicle-content">
      <div>
        <strong>Автомобиль</strong>
        {vehicle ? (
          <span className="carrier-profile__checked-value">
            <img src="/figma/assets/profile-check.svg" alt="" aria-hidden="true" />
            <span>{[vehicle.make_model, vehicle.body_type ? `(${vehicle.body_type})` : ''].filter(Boolean).join(' ')}</span>
          </span>
        ) : null}
      </div>
      {features.length > 0 ? (
        <div>
          <strong>Особенности</strong>
          {features.slice(0, 2).map((feature) => (
            <span className="carrier-profile__checked-value" key={feature}>
              <img src="/figma/assets/profile-check.svg" alt="" aria-hidden="true" />
              <span>{feature}</span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function PublicAboutVehicle({
  figmaNode,
  profile,
  size,
}: {
  figmaNode: string;
  profile: CarrierProfileDto;
  size: FigmaBreakpoint;
}) {
  const hasVehiclePhotos = Boolean(profile.vehicle_photos && profile.vehicle_photos.length > 0);

  return (
    <div className="carrier-profile__about-vehicle" data-figma-node={figmaNode}>
      <section className="carrier-profile__section carrier-profile__about">
        <h2>Обо мне</h2>
        <p>{profile.about}</p>
      </section>
      <section className="carrier-profile__section carrier-profile__vehicle">
        <div className="carrier-profile__section-heading">
          <h2>Транспорт</h2>
          {hasVehiclePhotos ? (
            <Button
              kind="tertiary"
              size="medium"
              icon="up-right"
              onClick={() => openPortfolioGallery(profile.vehicle_photos, 0)}
            >
              Смотреть фото
            </Button>
          ) : null}
        </div>
        <VehicleContent profile={profile} />
        {hasVehiclePhotos ? (
          <div className="carrier-profile__vehicle-photos-preview carrier-profile__public-vehicle-photos">
            {profile.vehicle_photos.map((photo, index) => (
              <button className="carrier-profile__vehicle-photo-thumb" key={photo.id} type="button"
                onClick={() => openPortfolioGallery(profile.vehicle_photos, index)}>
                <img src={photo.url} alt={photo.original_filename || 'Фото автомобиля'} />
              </button>
            ))}
          </div>
        ) : null}
      </section>
    </div>
  );
}

function RecentTrips({
  figmaNode,
  profile,
}: {
  figmaNode: string;
  profile: CarrierProfileDto;
}) {
  const track = useRef<HTMLDivElement>(null);
  const { canScrollLeft, canScrollRight, hasOverflow } = useTrackScroll(track, [profile.recent_trips.length]);

  function scrollTrip(direction: -1 | 1) {
    const element = track.current;
    if (!element) return;
    if (!window.matchMedia('(max-width: 479px)').matches) {
      scrollTrack(track, direction);
      return;
    }
    const cards = Array.from(element.querySelectorAll<HTMLElement>('.carrier-profile__trip'));
    if (!cards.length) return;
    const offsets = cards.map((card) => card.offsetLeft - cards[0].offsetLeft);
    const current = offsets.reduce((nearest, offset, index) => (
      Math.abs(offset - element.scrollLeft) < Math.abs(offsets[nearest] - element.scrollLeft) ? index : nearest
    ), 0);
    const next = Math.max(0, Math.min(cards.length - 1, current + direction));
    element.scrollTo({ left: offsets[next], behavior: 'smooth' });
  }

  return (
    <section className="carrier-profile__section carrier-profile__trips" data-figma-node={figmaNode}>
      <div className="carrier-profile__title-with-count">
        <h2>Недавние поездки</h2>
        <Tag type="tag" icon="no" label={`${profile.recent_trips.length} поездок`} />
      </div>
      {profile.recent_trips.length > 0 ? (
        <>
          <div className="carrier-profile__trip-track" ref={track}>
            {profile.recent_trips.map((trip, index) => (
              <Fragment key={`${trip.from_city}-${trip.to_city}-${trip.completed_at}-${index}`}>
                <article className="carrier-profile__trip">
                  <img
                    className="carrier-profile__trip-pet"
                    src={RECENT_TRIP_ICON[trip.pet_type] ?? RECENT_TRIP_ICON.other}
                    alt=""
                    aria-hidden="true"
                  />
                  <div className="carrier-profile__trip-details">
                    <div className="carrier-profile__trip-meta">
                      <time dateTime={trip.completed_at}>{recentTripDate(trip.completed_at)}</time>
                      <span aria-hidden="true">・</span>
                      <strong>{trip.pet_name}</strong>
                    </div>
                    <div className="carrier-profile__trip-route">
                      <span>{trip.from_city}</span>
                      <span className="carrier-profile__trip-route-arrow" aria-hidden="true" />
                      <span>{trip.to_city}</span>
                    </div>
                  </div>
                  <span className="carrier-profile__trip-open" aria-hidden="true" />
                </article>
                {index < profile.recent_trips.length - 1 && (
                  <span className="carrier-profile__trip-connector" aria-hidden="true" />
                )}
              </Fragment>
            ))}
          </div>
          {hasOverflow ? (
            <div className="carrier-profile__slider-controls">
              <SliderButton aria-label="Предыдущие поездки" direction="left" state={canScrollLeft ? 'Default' : 'disabled'} onClick={() => scrollTrip(-1)} />
              <SliderButton aria-label="Следующие поездки" direction="right" state={canScrollRight ? 'Default' : 'disabled'} onClick={() => scrollTrip(1)} />
            </div>
          ) : null}
        </>
      ) : (
        <div className="carrier-profile__empty-trips">
          <h3 className="carrier-profile__empty-trips-title">Будущие поездки появятся здесь</h3>
          <p className="carrier-profile__empty-trips-text">
            После выполнения заказов здесь будет отображаться история маршрутов.
          </p>
        </div>
      )}
    </section>
  );
}

function openPortfolioGallery(photos: CarrierProfileDto['portfolio_photos'], initialIndex: number) {
  import('photoswipe').then(async ({ default: PhotoSwipe }) => {
    await import('photoswipe/style.css');
    const items = photos.map((photo) => ({
      src: photo.url,
      caption: photo.caption || '',
      alt: photo.caption || photo.original_filename || 'Фото из портфолио',
    }));

    const lightbox = new PhotoSwipe({
      dataSource: items,
      index: initialIndex,
      bgOpacity: 0.92,
      wheelToZoom: true,
      closeOnVerticalDrag: true,
      padding: { top: 40, bottom: 80, left: 30, right: 30 },
      imageClickAction: 'zoom',
      returnFocus: false,
    });

    lightbox.on('gettingData', (event) => {
      const data = event.data;
      if (!data.width || !data.height) {
        const img = new Image();
        img.src = data.src as string;
        if (img.naturalWidth && img.naturalHeight) {
          data.width = img.naturalWidth;
          data.height = img.naturalHeight;
        } else {
          img.onload = () => {
            data.width = img.naturalWidth;
            data.height = img.naturalHeight;
            lightbox.refreshSlideContent(event.index);
          };
        }
      }
    });

    function renderCaption() {
      const pswp = lightbox.pswp;
      if (!pswp) return;
      const currIndex = pswp.currIndex ?? initialIndex;
      const item = items[currIndex];
      const captionText = item?.caption;
      let captionEl = document.querySelector<HTMLElement>('.lovo-pswp-caption');
      if (!captionEl) {
        captionEl = document.createElement('div');
        captionEl.className = 'lovo-pswp-caption';
        document.body.appendChild(captionEl);
      }
      if (captionText && typeof captionText === 'string' && captionText.trim()) {
        captionEl.textContent = captionText.trim();
        captionEl.style.setProperty('display', 'block', 'important');
      } else {
        captionEl.style.setProperty('display', 'none', 'important');
      }
    }

    lightbox.on('change', renderCaption);
    lightbox.on('contentActivate', renderCaption);
    lightbox.on('afterInit', () => {
      renderCaption();
      setTimeout(renderCaption, 50);
      setTimeout(renderCaption, 300);
    });

    lightbox.on('close', () => {
      const captionEl = document.querySelector<HTMLElement>('.lovo-pswp-caption');
      if (captionEl) captionEl.remove();
    });

    lightbox.init();
  });
}

function PublicPortfolio({
  figmaNode,
  limited,
  profile,
  size,
}: {
  figmaNode: string;
  limited: boolean;
  profile: CarrierProfileDto;
  size: FigmaBreakpoint;
}) {
  const grid = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const buttonSize = size === '375' ? 'medium' : 'big';
  const minCols = size === '375' ? 2 : size === '768' ? 3 : 5;
  const isSingleRow = !expanded && profile.portfolio_photos.length <= minCols;
  const canExpand = profile.portfolio_photos.length > 2 * minCols;
  const { canScrollLeft, canScrollRight, hasOverflow } = useTrackScroll(grid, [profile.portfolio_photos.length, size, expanded]);

  const displayPhotos = useMemo(() => {
    const photos = profile.portfolio_photos;
    if (expanded) {
      return photos.map((p, origIndex) => ({
        id: `photo-${p.id}`,
        isEmpty: false,
        origIndex,
        photo: p,
      }));
    }

    if (photos.length <= minCols) {
      const ordered: Array<{
        id: string;
        isEmpty: boolean;
        origIndex?: number;
        photo?: (typeof photos)[number];
      }> = [];
      for (let col = 0; col < minCols; col++) {
        if (col < photos.length) {
          ordered.push({
            id: `photo-${photos[col].id}`,
            isEmpty: false,
            origIndex: col,
            photo: photos[col],
          });
        } else {
          ordered.push({
            id: `empty-top-${col}`,
            isEmpty: true,
          });
        }
      }
      return ordered;
    }

    const requiredCols = Math.max(minCols, Math.ceil(photos.length / 2));
    const topRowCapacity = minCols;
    const topRowPhotos = photos.slice(0, topRowCapacity);
    const bottomRowPhotos = photos.slice(topRowCapacity);

    const ordered: Array<{
      id: string;
      isEmpty: boolean;
      origIndex?: number;
      photo?: (typeof photos)[number];
    }> = [];

    for (let col = 0; col < requiredCols; col++) {
      if (col < topRowPhotos.length) {
        ordered.push({
          id: `photo-${topRowPhotos[col].id}`,
          isEmpty: false,
          origIndex: col,
          photo: topRowPhotos[col],
        });
      } else {
        ordered.push({
          id: `empty-top-${col}`,
          isEmpty: true,
        });
      }

      if (col < bottomRowPhotos.length) {
        ordered.push({
          id: `photo-${bottomRowPhotos[col].id}`,
          isEmpty: false,
          origIndex: topRowCapacity + col,
          photo: bottomRowPhotos[col],
        });
      } else if (col < minCols) {
        ordered.push({
          id: `empty-bottom-${col}`,
          isEmpty: true,
        });
      }
    }

    return ordered;
  }, [profile.portfolio_photos, expanded, minCols]);

  return (
    <section className="carrier-profile__section carrier-profile__portfolio" data-figma-node={figmaNode}>
      <div className="carrier-profile__title-with-count">
        <h2>Портфолио</h2>
        <Tag type="tag" icon="no" label={`${profile.portfolio_photos_count ?? profile.portfolio_photos.length} фото`} />
      </div>
      <div
        className={`carrier-profile__photo-grid ${expanded ? 'is-expanded' : ''} ${isSingleRow ? 'carrier-profile__photo-grid--single-row' : ''}`}
        ref={grid}
      >
        {displayPhotos.map((item) => (
          item.isEmpty || !item.photo ? (
            <PhotoCard
              key={item.id}
              size={size}
              state="empty"
            />
          ) : (
            <PhotoCard
              alt={item.photo.caption || item.photo.original_filename || 'Фото из портфолио перевозчика'}
              caption={item.photo.caption}
              imageUrl={item.photo.url}
              key={item.id}
              size={size}
              state="Default"
              onClick={() => typeof item.origIndex === 'number' && openPortfolioGallery(profile.portfolio_photos, item.origIndex)}
            />
          )
        ))}
      </div>
      {canExpand || (!expanded && hasOverflow) ? (
        <div className="carrier-profile__portfolio-controls">
          {canExpand ? (
            <Button
              kind="tertiary"
              size={buttonSize}
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? 'Свернуть фотографии' : 'Показать все фотографии'}
            </Button>
          ) : <div />}
          {!expanded && hasOverflow ? (
            <div className="carrier-profile__slider-controls">
              <SliderButton aria-label="Предыдущие фотографии" direction="left" state={canScrollLeft ? 'Default' : 'disabled'} onClick={() => scrollTrack(grid, -1)} />
              <SliderButton aria-label="Следующие фотографии" direction="right" state={canScrollRight ? 'Default' : 'disabled'} onClick={() => scrollTrack(grid, 1)} />
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function ProfileReviews({
  figmaNode,
  internal = false,
  profile,
  reviewsRef,
  size,
}: {
  figmaNode?: string;
  internal?: boolean;
  profile: CarrierProfileDto;
  reviewsRef: RefObject<HTMLElement | null>;
  size: FigmaBreakpoint;
}) {
  const { language } = useLanguage();
  const reviewTrack = useRef<HTMLDivElement>(null);
  const videoTrack = useRef<HTMLDivElement>(null);
  const videos = useMemo(() => profile.reviews.flatMap((review) => review.photos
    .filter((asset) => asset.content_type?.startsWith('video/'))
    .map((asset) => ({ asset, review }))), [profile.reviews]);
  const buttonSize = size === '375' ? 'medium' : 'big';

  const reviewsScroll = useTrackScroll(reviewTrack, [profile.reviews.length, size]);
  const videosScroll = useTrackScroll(videoTrack, [videos.length, size]);

  return (
    <section
      className={`carrier-profile__section carrier-profile__reviews ${internal ? 'carrier-profile__reviews--internal' : ''}`}
      data-figma-node={figmaNode ?? (internal ? '2867:35543' : PUBLIC_FIGMA[size].reviews)}
      ref={reviewsRef}
    >
      <div className="carrier-profile__review-block carrier-profile__review-block--text">
        <div className="carrier-profile__title-with-count">
          <h2>Отзывы</h2>
          <Tag type="tag" icon="no" label={String(profile.reviews_count ?? profile.reviews.length)} />
        </div>
        {profile.reviews.length > 0 ? (
          <>
            <div className="carrier-profile__review-track" ref={reviewTrack}>
              {profile.reviews.map((review) => (
                <ReviewCard
                  author={review.author_name}
                  date={shortDate(review.created_at, language)}
                  initials={initials(review.author_name)}
                  key={review.id}
                  rating={review.rating as StarsRating}
                  size={size}
                  sourceLabel={internal && review.moderation_status === 'pending'
                    ? language === 'en' ? 'Pending moderation' : 'На модерации'
                    : 'LOVO'}
                  text={review.comment}
                />
              ))}
            </div>
            <div className="carrier-profile__review-controls">
              <Button kind="tertiary" size={buttonSize} onClick={() => scrollTrack(reviewTrack, 1)}>
                Показать ещё отзывы
              </Button>
              {reviewsScroll.hasOverflow ? (
                <div className="carrier-profile__slider-controls">
                  <SliderButton aria-label="Предыдущие отзывы" direction="left" state={reviewsScroll.canScrollLeft ? 'Default' : 'disabled'} onClick={() => scrollTrack(reviewTrack, -1)} />
                  <SliderButton aria-label="Следующие отзывы" direction="right" state={reviewsScroll.canScrollRight ? 'Default' : 'disabled'} onClick={() => scrollTrack(reviewTrack, 1)} />
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <p className="carrier-profile__empty-feedback">
            {internal
              ? 'Пока нет. Они появятся здесь после ваших первых завершённых поездок.'
              : 'У перевозчика пока нет отзывов. Вы можете стать первым заказчиком!'}
          </p>
        )}
      </div>

      <div className="carrier-profile__review-block carrier-profile__review-block--video">
        <div className="carrier-profile__title-with-count">
          <h2>Видеоотзывы</h2>
          <Tag type="tag" icon="no" label={String(videos.length)} />
        </div>
        {videos.length > 0 ? (
          <>
            <div className="carrier-profile__video-track" ref={videoTrack}>
              {videos.map(({ asset, review }) => (
                <VideoCard
                  key={asset.id}
                  rating={review.rating as StarsRating}
                  size={size}
                  title={videoReviewTitle(asset, review)}
                  videoUrl={asset.url}
                />
              ))}
            </div>
            {videosScroll.hasOverflow ? (
              <div className="carrier-profile__review-controls carrier-profile__review-controls--video">
                <span />
                <div className="carrier-profile__slider-controls">
                  <SliderButton aria-label="Предыдущие видеоотзывы" direction="left" state={videosScroll.canScrollLeft ? 'Default' : 'disabled'} onClick={() => scrollTrack(videoTrack, -1)} />
                  <SliderButton aria-label="Следующие видеоотзывы" direction="right" state={videosScroll.canScrollRight ? 'Default' : 'disabled'} onClick={() => scrollTrack(videoTrack, 1)} />
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <p className="carrier-profile__empty-feedback">
            {internal
              ? 'Пока нет. Клиенты смогут прикреплять видео к отзывам о поездке.'
              : 'Клиенты пока не добавили видео к отзывам.'}
          </p>
        )}
      </div>
    </section>
  );
}

function PublicProfileContent({ ownPreview, profile }: { ownPreview: boolean; profile: CarrierProfileDto }) {
  const size = useFigmaBreakpoint();
  const [menuOpen, setMenuOpen] = useState(false);
  const reviewsRef = useRef<HTMLElement>(null);
  const limited = size === '1920' && profile.portfolio_photos.length <= 5;
  const figma = limited ? PUBLIC_LOW_PHOTO_FIGMA : PUBLIC_FIGMA[size];

  return (
    <div
      className={`carrier-profile carrier-profile--public${limited ? ' carrier-profile--limited-photos' : ''}`}
      data-screen="carrier-profile-external"
      data-figma-node={figma.root}
      data-figma-variant={limited ? '1920, photos=limited' : size}
    >
      <ProfileNavigation accountLabel={profile.display_name} menuOpen={menuOpen} onMenuOpen={setMenuOpen} />

      {ownPreview ? (
        <div className="carrier-profile__preview" data-figma-node={figma.alert}>
          <div className="carrier-profile__preview-alert" data-figma-node="137:34358">
            <img src="/figma/assets/profile-alert-circle.svg" alt="" aria-hidden="true" />
            <div>
              <span>Это превью — так ваш профиль выглядит для заказчика, открывшего ваш отклик.</span>
              <span>Личные данные (паспорт, номер карты, телефон) не отображаются</span>
            </div>
          </div>
          <Button kind="tertiary" size={size === '375' ? 'medium' : 'big'} icon="left" onClick={() => navigate('carrier-profile-internal')}>
            Вернуться в личный кабинет
          </Button>
        </div>
      ) : null}

      <main className="carrier-profile__public-main" data-figma-node={figma.main}>
        <ProfileHero figmaNode={figma.hero} profile={profile} size={size} onReviews={() => reviewsRef.current?.scrollIntoView({ behavior: 'smooth' })} />
        <PublicAboutVehicle figmaNode={figma.aboutVehicle} profile={profile} size={size} />
        <RecentTrips figmaNode={figma.trips} profile={profile} />
        <PublicPortfolio figmaNode={figma.portfolio} limited={limited} profile={profile} size={size} />
        <ProfileReviews figmaNode={figma.reviews} profile={profile} reviewsRef={reviewsRef} size={size} />
        {ownPreview ? (
          <div className="carrier-profile__public-back" data-figma-node={figma.back}>
            <Button kind="tertiary" size="medium" icon="left" onClick={() => navigate('carrier-profile-internal')}>
              Вернуться в личный кабинет
            </Button>
          </div>
        ) : null}
      </main>

      <ProfileFooter size={size} />
    </div>
  );
}

function PublicProfileScreen() {
  const auth = useAuth();
  const [profile, setProfile] = useState<CarrierProfileDto | null>(null);
  const requestedId = hashNumber('id');

  useEffect(() => {
    if (auth.status === 'loading') return;
    if (!requestedId && (auth.status !== 'authenticated' || auth.user?.active_role !== 'carrier')) return;
    const request = requestedId ? carrierCabinetApi.publicProfile(requestedId) : carrierCabinetApi.profile();
    void request.then(setProfile).catch(() => setProfile(null));
  }, [auth.status, auth.user?.active_role, requestedId]);

  if (!profile) return null;
  const isCarrier = auth.status === 'authenticated' && auth.user?.active_role === 'carrier';
  const isOwn = !requestedId || (isCarrier && auth.user?.id === profile.id);
  return <PublicProfileContent ownPreview={Boolean(isCarrier && isOwn)} profile={profile} />;
}

const SIDEBAR_GROUPS = [
  {
    title: 'Работа',
    items: [
      ['carrier-dashboard', 'Главная'],
      ['carrier-search', 'Поиск заявок'],
      ['carrier-my-offers', 'Мои отклики'],
      ['carrier-active-transport', 'Активная перевозка'],
      ['carrier-completed', 'История'],
    ],
  },
  {
    title: 'Аккаунт',
    items: [
      ['carrier-profile-internal', 'Профиль'],
      ['carrier-earnings', 'Счета и расчёты'],
      ['carrier-documents', 'Документы'],
      ['carrier-help', 'Помощь'],
    ],
  },
] as const;

function CarrierProfileSidebar({ editing }: { editing: boolean }) {
  return (
    <aside className="carrier-profile__sidebar" data-figma-node={editing ? '2867:34352' : '2867:35434'} data-figma-variant="who=carrier, icons=off">
      {SIDEBAR_GROUPS.map((group, index) => (
        <div className="carrier-profile__sidebar-group" key={'title' in group ? group.title : `group-${index}`}>
          {'title' in group ? <strong>{group.title}</strong> : null}
          <nav aria-label={'title' in group ? group.title : 'Раздел кабинета'}>
            {group.items.map(([screen, label]) => (
              <button className={screen === 'carrier-profile-internal' ? 'is-active' : ''} key={screen} type="button" onClick={() => navigate(screen)}>
                {label}
              </button>
            ))}
          </nav>
        </div>
      ))}
    </aside>
  );
}

interface ProfileFormState {
  about: string;
  first_name: string;
  last_name: string;
  vehicle_make_model: string;
  vehicle_pet_features: string;
  vehicle_year: string;
}

type ProfileHelpTopic = 'about' | 'portfolio';

function ProfileHelpModal({ onClose, topic }: { onClose: () => void; topic: ProfileHelpTopic }) {
  const closeButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButton.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const title = topic === 'portfolio'
    ? 'Требования к фотографиям портфолио'
    : 'Как написать хороший текст о себе';

  return createPortal(
    <div className="carrier-profile-help__backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section aria-labelledby="carrier-profile-help-title" aria-modal="true" className="carrier-profile-help" role="dialog">
        <div className="carrier-profile-help__heading">
          <h2 id="carrier-profile-help-title">{title}</h2>
          <button ref={closeButton} aria-label="Закрыть" className="carrier-profile-help__close" type="button" onClick={onClose}>
            <X aria-hidden="true" size={24} strokeWidth={1.5} />
          </button>
        </div>
        {topic === 'portfolio' ? (
          <div className="carrier-profile-help__content">
            <p>Покажите реальные условия перевозки и заботу о питомцах. После модерации фотографии увидят заказчики.</p>
            <h3>Какие фотографии подходят</h3>
            <ul>
              <li>Питомец в дороге, при передаче или прибытии.</li>
              <li>Переноска, салон автомобиля и безопасное размещение.</li>
              <li>Кормление, прогулка и другие этапы поездки.</li>
            </ul>
            <h3>Технические требования</h3>
            <ul>
              <li>JPG, PNG или WEBP размером до 10 МБ.</li>
              <li>Чёткое изображение без сильного размытия и водяных знаков.</li>
              <li>Без телефонов, email и других контактных данных в кадре или подписи.</li>
            </ul>
            <p className="carrier-profile-help__note">В рейтинг входят одобренные фотографии: каждая добавляет 2,5%, 40 фотографий дают 100%.</p>
          </div>
        ) : (
          <div className="carrier-profile-help__content">
            <p>Напишите 3–6 коротких предложений, которые помогут заказчику понять ваш опыт и подход.</p>
            <h3>Расскажите главное</h3>
            <ul>
              <li>Сколько лет вы перевозите животных и по каким направлениям.</li>
              <li>Как обеспечиваете безопасность, комфорт и связь в дороге.</li>
              <li>На каком транспорте работаете и с какими питомцами есть опыт.</li>
            </ul>
            <h3>Не публикуйте</h3>
            <ul>
              <li>Телефон, email, ссылки на мессенджеры и соцсети.</li>
              <li>Цены и обещания, которые нельзя подтвердить.</li>
            </ul>
            <p className="carrier-profile-help__example"><strong>Пример:</strong> Бережно перевожу собак и кошек по России и Европе. В дороге поддерживаю привычный режим питомца, регулярно отправляю фото и всегда остаюсь на связи с заказчиком.</p>
          </div>
        )}
        <Button kind="primary" size="medium" onClick={onClose}>Понятно</Button>
      </section>
    </div>,
    document.body,
  );
}

function formState(profile: CarrierProfileDto): ProfileFormState {
  const vehicle = primaryVehicle(profile);
  return {
    about: profile.about ?? '',
    first_name: profile.first_name ?? profile.contact_name ?? '',
    last_name: profile.last_name ?? profile.company_name ?? '',
    vehicle_make_model: vehicle?.make_model ?? '',
    vehicle_pet_features: vehicle?.pet_features ?? '',
    vehicle_year: vehicle?.year ? String(vehicle.year) : '',
  };
}

function InternalContactSection({
  emailState,
  emailVerified,
  editing,
  form,
  onCancel,
  onChange,
  onEdit,
  onSubmit,
  onVerifyEmail,
  onPhoneVerified,
  profile,
}: {
  emailState: 'idle' | 'sending' | 'sent' | 'error';
  emailVerified: boolean;
  editing: boolean;
  form: ProfileFormState;
  onCancel: () => void;
  onChange: (field: keyof ProfileFormState, value: string) => void;
  onEdit: () => void;
  onSubmit: (event: FormEvent) => void;
  onVerifyEmail: () => void;
  onPhoneVerified: (phone: string) => void;
  profile: CarrierProfileDto;
}) {
  const [helpOpen, setHelpOpen] = useState(false);
  const { user } = useAuth();
  return (
    <section className={`carrier-profile__section carrier-profile__contact ${editing ? 'is-editing' : ''}`} data-figma-node={editing ? '2867:34402' : '2867:35483'}>
      <h2>Контактные данные</h2>
      {editing ? (
        <form className="carrier-profile__contact-form" onSubmit={onSubmit}>
          <label><span>Имя</span><input value={form.first_name} onChange={(event) => onChange('first_name', event.target.value)} /></label>
          <label><span>Фамилия</span><input value={form.last_name} onChange={(event) => onChange('last_name', event.target.value)} /></label>
          <ProfilePhoneField labelPosition="above" value={profile.phone ?? ''} onVerified={onPhoneVerified} />
          <label><span>E-mail</span><input disabled value={profile.email ?? ''} /></label>
          <div className="carrier-profile__edit-about-heading">
            <div>
              <h2>Обо мне</h2>
              <Tag type="tag" icon="no" label="Будет видно пользователям" />
            </div>
            <CabinetHelpLink onClick={() => setHelpOpen(true)}>
              Как написать хороший текст о себе
            </CabinetHelpLink>
          </div>
          <textarea aria-label="Обо мне" value={form.about} onChange={(event) => onChange('about', event.target.value)} />
          <div className="carrier-profile__edit-actions">
            <Button type="submit" kind="primary" size="medium">Сохранить изменения</Button>
            <Button kind="tertiary" size="medium" icon="none" onClick={onCancel}>Отменить</Button>
          </div>
        </form>
      ) : (
        <>
          <div className="carrier-profile__contact-lines">
            <span>{profile.display_name}</span>
            <span>{maskedPhone(profile.phone)}</span>
            {user?.phone_verified_at && user.phone === profile.phone ? <Tag type="Verification" icon="no" label="Телефон подтверждён" /> : null}
            <span className="carrier-profile__email-line">
              <span>{profile.email}</span>
              {emailVerified ? (
                <Tag
                  type="Verification"
                  icon="no"
                  label="Email подтверждён"
                  className="carrier-profile__email-tag"
                />
              ) : (
                <Button disabled={emailState === 'sending'} kind="tertiary" size="medium" onClick={onVerifyEmail}>
                  {emailState === 'sent' ? 'Письмо отправлено' : emailState === 'error' ? 'Повторить отправку' : 'Подтвердить email'}
                </Button>
              )}
            </span>
          </div>
          <div className="carrier-profile__internal-about-heading">
            <div>
              <h2>Обо мне</h2>
              <Tag type="tag" icon="no" label="Будет видно пользователям" />
            </div>
            <CabinetHelpLink onClick={() => setHelpOpen(true)}>
              Как написать хороший текст о себе
            </CabinetHelpLink>
          </div>
          {profile.about?.trim() ? (
            <p>{profile.about.trim()}</p>
          ) : (
            <p className="carrier-profile__about-placeholder">
              Расскажите о себе, вашем опыте и подходе к перевозке...
            </p>
          )}
          <Button kind="tertiary" size="big" icon="up-right" onClick={onEdit}>Редактировать</Button>
        </>
      )}
      {helpOpen ? <ProfileHelpModal topic="about" onClose={() => setHelpOpen(false)} /> : null}
    </section>
  );
}

function InternalVehicleSection({
  editing,
  form,
  onCancel,
  onChange,
  onDeletePhoto,
  onEdit,
  onSubmit,
  onUploadPhoto,
  profile,
  uploading = false,
}: {
  editing: boolean;
  form: ProfileFormState;
  onCancel: () => void;
  onChange: (field: keyof ProfileFormState, value: string) => void;
  onDeletePhoto: (id: number) => void;
  onEdit: () => void;
  onSubmit: (event: FormEvent) => void;
  onUploadPhoto: (files: File[]) => void;
  profile: CarrierProfileDto;
  uploading?: boolean;
}) {
  const vehicle = primaryVehicle(profile);
  const features = splitFeatures(form.vehicle_pet_features);
  const fileInput = useRef<HTMLInputElement>(null);

  function handleChoosePhoto(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    if (files.length > 0) onUploadPhoto(files);
    event.target.value = '';
  }

  return (
    <section className={`carrier-profile__section carrier-profile__internal-vehicle ${editing ? 'is-editing' : ''}`} data-figma-node={editing ? '2867:34431' : '2867:35503'}>
      <h2>Транспорт</h2>
      {editing ? (
        <form className="carrier-profile__vehicle-form" onSubmit={onSubmit}>
          <div className="carrier-profile__vehicle-fields">
            <strong>Автомобиль</strong>
            <label><span>Марка и модель</span><input value={form.vehicle_make_model} onChange={(event) => onChange('vehicle_make_model', event.target.value)} /></label>
            <label><span>Год выпуска</span><input inputMode="numeric" value={form.vehicle_year} onChange={(event) => onChange('vehicle_year', event.target.value)} /></label>
          </div>
          <div className="carrier-profile__vehicle-features">
            <strong>Особенности</strong>
            <label><span>Впишите через запятую</span><input value={form.vehicle_pet_features} onChange={(event) => onChange('vehicle_pet_features', event.target.value)} /></label>
            {features.length > 0 && (
              <div className="carrier-profile__feature-chips">
                {features.map((feature, index) => (
                  <Tag
                    key={`${feature}-${index}`}
                    type="Chips"
                    icon="Right"
                    label={feature}
                    onRemove={() => {
                      const next = features.filter((_, i) => i !== index).join(', ');
                      onChange('vehicle_pet_features', next);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="carrier-profile__vehicle-photos-edit">
            <strong>Фотографии автомобиля</strong>
            <div className="carrier-profile__vehicle-photos-grid">
              <button
                className={`carrier-profile__add-vehicle-photo ${uploading ? 'is-loading' : ''}`}
                disabled={uploading}
                type="button"
                onClick={() => fileInput.current?.click()}
              >
                {uploading ? (
                  <span className="carrier-profile__upload-spinner">
                    <Loader2 className="animate-spin" size={16} />
                    Загрузка…
                  </span>
                ) : (
                  '+ Добавить фото'
                )}
              </button>
              {profile.vehicle_photos?.map((photo) => (
                <div className="carrier-profile__editable-vehicle-photo" key={photo.id}>
                  <img src={photo.url} alt={photo.original_filename || 'Фото авто'} />
                  <button
                    type="button"
                    aria-label="Удалить фото автомобиля"
                    onClick={() => onDeletePhoto(photo.id)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <input
              ref={fileInput}
              hidden
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleChoosePhoto}
            />
          </div>
          <div className="carrier-profile__edit-actions">
            <Button type="submit" kind="primary" size="medium">Сохранить изменения</Button>
            <Button kind="tertiary" size="medium" icon="none" onClick={onCancel}>Отменить</Button>
          </div>
        </form>
      ) : (
        <>
          <div className="carrier-profile__internal-vehicle-data">
            <div><strong>Автомобиль</strong><span>{[vehicle?.make_model, vehicle?.body_type, vehicle?.year].filter(Boolean).join('\n') || 'Не указан'}</span></div>
            {features.length > 0 ? (
              <div className="carrier-profile__vehicle-features-col"><strong>Особенности</strong><span>{features.join('\n')}</span></div>
            ) : null}
          </div>
          {profile.vehicle_photos && profile.vehicle_photos.length > 0 ? (
            <div className="carrier-profile__vehicle-photos-view">
              <strong>Фотографии автомобиля</strong>
              <div className="carrier-profile__vehicle-photos-preview">
                {profile.vehicle_photos.map((photo, index) => (
                  <button
                    className="carrier-profile__vehicle-photo-thumb"
                    key={photo.id}
                    type="button"
                    onClick={() => openPortfolioGallery(profile.vehicle_photos, index)}
                  >
                    <img src={photo.url} alt={photo.original_filename || 'Фото автомобиля'} />
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          <Button kind="tertiary" size="big" icon="up-right" onClick={onEdit}>Редактировать</Button>
        </>
      )}
    </section>
  );
}

function InternalPortfolio({
  onDelete,
  onUpdateCaption,
  onUpload,
  profile,
  uploading = false,
}: {
  onDelete: (id: number) => void;
  onUpdateCaption: (id: number, caption: string) => Promise<void>;
  onUpload: (files: File[]) => void;
  profile: CarrierProfileDto;
  uploading?: boolean;
}) {
  const { language } = useLanguage();
  const input = useRef<HTMLInputElement>(null);
  const [editingPhotoId, setEditingPhotoId] = useState<number | null>(null);
  const [captionValue, setCaptionValue] = useState('');
  const [savingCaption, setSavingCaption] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const photoCount = profile.portfolio_photos_count ?? profile.portfolio_photos.length;
  const nodes = ['2867:35532', '2867:35533', '2867:35534', '2867:35535', '2867:35537', '2867:35538', '2867:35539', '2867:35540', '2867:35541'];

  function chooseFiles(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    if (files.length > 0) onUpload(files);
    event.target.value = '';
  }

  function startEditCaption(photo: CarrierProfileDto['portfolio_photos'][number]) {
    setEditingPhotoId(photo.id);
    setCaptionValue(photo.caption || '');
  }

  async function handleSaveCaption(id: number) {
    setSavingCaption(true);
    try {
      await onUpdateCaption(id, captionValue.trim());
      setEditingPhotoId(null);
    } finally {
      setSavingCaption(false);
    }
  }

  return (
    <section className="carrier-profile__section carrier-profile__internal-portfolio" data-figma-node="2867:35514">
      <div className="carrier-profile__internal-portfolio-heading" data-figma-node="2867:35515">
        <div className="carrier-profile__title-with-count">
          <h2>Портфолио</h2>
          <Tag
            type="tag"
            icon="no"
            label={language === 'en'
              ? `${photoCount} ${photoCount === 1 ? 'photo' : 'photos'}`
              : `${photoCount} фото`}
          />
        </div>
        <CabinetHelpLink onClick={() => setHelpOpen(true)}>
          Требования к загружаемым фотографиям
        </CabinetHelpLink>
      </div>
      <div className="carrier-profile__portfolio-alert" data-figma-node="2867:35529" data-i18n-skip>
        <img src="/figma/assets/profile-alert-circle.svg" alt="" aria-hidden="true" />
        <div>
          <strong>
            {language === 'en'
              ? `Portfolio rating: ${profile.photo_rating_percent}%. More varied photos improve your search ranking and build customer trust.`
              : `Рейтинг портфолио: ${profile.photo_rating_percent}%. Чем больше и разнообразнее фото — тем выше место в выдаче и доверие заказчиков.`}
          </strong>
          <span>{language === 'en' ? 'Upload photos for every completed transport.' : 'Загружайте фотографии к каждой завершённой перевозке.'}</span>
        </div>
      </div>
      <div className="carrier-profile__internal-photo-grid" data-figma-node="2867:35530">
        <button
          className={`carrier-profile__add-photo ${uploading ? 'is-loading' : ''}`}
          data-i18n-skip
          data-figma-node="2867:34152"
          disabled={uploading}
          type="button"
          onClick={() => input.current?.click()}
        >
          {uploading ? (
            <span className="carrier-profile__upload-spinner">
              <Loader2 className="animate-spin" size={18} />
              {language === 'en' ? 'Uploading...' : 'Загрузка…'}
            </span>
          ) : (
            language === 'en' ? '+ Add' : '+ Добавить'
          )}
        </button>
        {profile.portfolio_photos.map((photo, index) => (
          <div className="carrier-profile__editable-photo" data-figma-node={nodes[index % nodes.length]} key={photo.id}>
            <img
              src={photo.url}
              alt={photo.caption || photo.original_filename || 'Фото из портфолио'}
              role="button"
              tabIndex={0}
              title="Нажмите для просмотра в полном размере"
              onClick={() => openPortfolioGallery(profile.portfolio_photos, index)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openPortfolioGallery(profile.portfolio_photos, index);
                }
              }}
            />
            {editingPhotoId === photo.id ? (
              <div className="carrier-profile__caption-form">
                <input
                  autoFocus
                  placeholder="Описание фото..."
                  value={captionValue}
                  onChange={(e) => setCaptionValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleSaveCaption(photo.id);
                    if (e.key === 'Escape') setEditingPhotoId(null);
                  }}
                />
                <button disabled={savingCaption} type="button" onClick={() => void handleSaveCaption(photo.id)}>✓</button>
                <button type="button" onClick={() => setEditingPhotoId(null)}>✕</button>
              </div>
            ) : (
              <div>
                <button
                  data-i18n-skip={Boolean(photo.caption?.trim()) || undefined}
                  type="button"
                  onClick={() => startEditCaption(photo)}
                  title={photo.caption || 'Добавить описание'}
                >
                  {photo.caption?.trim() ? photo.caption.trim() : 'Добавить описание'}
                </button>
                <button type="button" aria-label={`Удалить ${photo.original_filename || 'фотографию'}`} onClick={() => onDelete(photo.id)}>
                  <img src="/figma/assets/profile-trash.svg" alt="" aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      <input ref={input} hidden type="file" multiple accept="image/jpeg,image/png,image/webp" onChange={chooseFiles} />
      {profile.portfolio_photos.length > 10 ? (
        <Button kind="tertiary" size="big" icon="up-right">Показать все фотографии</Button>
      ) : null}
      {helpOpen ? <ProfileHelpModal topic="portfolio" onClose={() => setHelpOpen(false)} /> : null}
    </section>
  );
}

const DOCUMENT_ROWS: Array<{ type: DocumentType; label: string; index?: number }> = [
  { type: 'identity', label: 'Паспорт' },
  { type: 'selfie', label: 'Селфи с паспортом' },
  { type: 'driver_license', label: 'Водительские права' },
  { type: 'vehicle_registration', label: 'СТС / регистрация автомобиля' },
  { type: 'company', label: 'Документы компании' },
  { type: 'license', label: 'Лицензия ИП на грузоперевозки (1 из 2)', index: 0 },
  { type: 'license', label: 'Лицензия международные перевозки (2 из 2)', index: 1 },
];

function InternalDocuments({ documents, profile }: { documents: CarrierDocument[]; profile: CarrierProfileDto }) {
  return (
    <section className="carrier-profile__section carrier-profile__documents" data-figma-node="2867:35590">
      <h2>Документы и верификация</h2>
      <div className="carrier-profile__document-list">
        {DOCUMENT_ROWS.filter((row) => {
          if (row.type === 'vehicle_registration') return profile.service_type === 'auto';
          if (row.type === 'company') return profile.carrier_type === 'company';
          return true;
        }).map((row) => {
          const matches = documents
            .filter((document) => document.document_type === row.type)
            .sort((left, right) => right.id - left.id);
          const document = row.type === 'license'
            ? matches.find((item) => item.title.toLowerCase().includes('международ') === (row.index === 1))
            : matches[0];
          const status = document?.status ?? 'missing';
          return (
            <div className="carrier-profile__document-row" key={`${row.type}-${row.index ?? 0}`}>
              <span>{row.label}</span>
              <Tag
                type={status === 'approved' ? 'Verification' : 'tag'}
                icon={status === 'approved' ? 'left' : 'no'}
                label={DOCUMENT_STATUS_LABEL[status]}
              />
            </div>
          );
        })}
      </div>
      <p>Лицензий можно загрузить несколько. Для авиакурьеров лицензия не требуется — достаточно паспорта и подтверждённого опыта.</p>
      <Button kind="tertiary" size="big" icon="up-right" onClick={() => navigate('carrier-documents')}>Обновить документы</Button>
    </section>
  );
}

function InternalRoutes({
  onUpdateRoutes,
  profile,
}: {
  onUpdateRoutes: (routes: Array<{ from_city: string; to_city: string }>) => Promise<void>;
  profile: CarrierProfileDto;
}) {
  const [editing, setEditing] = useState(false);
  const [routes, setRoutes] = useState(profile.preferred_routes || []);
  const [fromCity, setFromCity] = useState('');
  const [toCity, setToCity] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRoutes(profile.preferred_routes || []);
  }, [profile.preferred_routes]);

  function addRoute() {
    if (!fromCity.trim() || !toCity.trim()) return;
    setRoutes((current) => [...current, { from_city: fromCity.trim(), to_city: toCity.trim() }]);
    setFromCity('');
    setToCity('');
  }

  function removeRoute(index: number) {
    setRoutes((current) => current.filter((_, i) => i !== index));
  }

  async function save() {
    setSaving(true);
    try {
      await onUpdateRoutes(routes);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setRoutes(profile.preferred_routes || []);
    setFromCity('');
    setToCity('');
    setEditing(false);
  }

  return (
    <section className="carrier-profile__section carrier-profile__routes" data-figma-node="2867:35611">
      <h2>Предпочитаемые маршруты</h2>
      {editing ? (
        <div className="carrier-profile__routes-form">
          <p>Добавьте маршруты, по которым вы регулярно выполняете поездки</p>
          <div className="carrier-profile__routes-inputs">
            <div className="carrier-profile__route-field">
              <span>Откуда</span>
              <input
                placeholder="Город отправления"
                value={fromCity}
                onChange={(e) => setFromCity(e.target.value)}
              />
            </div>
            <div className="carrier-profile__route-field">
              <span>Куда</span>
              <input
                placeholder="Город прибытия"
                value={toCity}
                onChange={(e) => setToCity(e.target.value)}
              />
            </div>
            <Button
              kind="secondary"
              size="medium"
              icon="none"
              onClick={addRoute}
              disabled={!fromCity.trim() || !toCity.trim()}
            >
              + Добавить маршрут
            </Button>
          </div>

          <div className="carrier-profile__route-tags carrier-profile__route-tags--editable">
            {routes.map((route, index) => (
              <span className="carrier-profile__route-chip" key={`${route.from_city}-${route.to_city}-${index}`}>
                <span>{route.from_city} — {route.to_city}</span>
                <button type="button" aria-label="Удалить маршрут" onClick={() => removeRoute(index)}>
                  ×
                </button>
              </span>
            ))}
            {routes.length === 0 ? (
              <span className="carrier-profile__empty-routes">Маршруты пока не добавлены</span>
            ) : null}
          </div>

          <div className="carrier-profile__edit-actions">
            <Button type="button" kind="primary" size="medium" disabled={saving} onClick={save}>
              Сохранить изменения
            </Button>
            <Button type="button" kind="tertiary" size="medium" icon="none" onClick={cancel}>
              Отменить
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <p>Получайте уведомления о новых заявках на этих маршрутах</p>
          <div className="carrier-profile__route-tags">
            {profile.preferred_routes.length > 0 ? (
              profile.preferred_routes.map((route, index) => (
                <Tag key={`${route.from_city}-${route.to_city}-${index}`} type="Chips" icon="no" label={`${route.from_city} — ${route.to_city}`} />
              ))
            ) : (
              <span className="carrier-profile__empty-routes">Маршруты не указаны</span>
            )}
          </div>
          <Button kind="tertiary" size="big" icon="up-right" onClick={() => setEditing(true)}>Изменить</Button>
        </div>
      )}
    </section>
  );
}

function InternalProfileContent() {
  const size = useFigmaBreakpoint();
  const auth = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profile, setProfile] = useState<CarrierProfileDto | null>(null);
  const [documents, setDocuments] = useState<CarrierDocument[]>([]);
  const [editingSection, setEditingSection] = useState<'contacts' | 'vehicle' | null>(null);
  const [emailState, setEmailState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [form, setForm] = useState<ProfileFormState | null>(null);
  const reviewsRef = useRef<HTMLElement>(null);

  async function load() {
    const [nextProfile, nextDocuments] = await Promise.all([
      carrierCabinetApi.profile(),
      getCarrierDocuments(),
    ]);
    setProfile(nextProfile);
    setDocuments(nextDocuments);
    setForm(formState(nextProfile));
  }

  useEffect(() => {
    void load().catch(() => {
      setProfile(null);
      setForm(null);
    });
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && editingSection) {
        cancelEdit();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingSection, profile]);

  function changeForm(field: keyof ProfileFormState, value: string) {
    setForm((current) => current ? { ...current, [field]: value } : current);
  }

  function cancelEdit() {
    if (profile) setForm(formState(profile));
    setEditingSection(null);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!form) return;
    const nextProfile = await carrierCabinetApi.updateProfile({
      about: form.about,
      first_name: form.first_name,
      last_name: form.last_name,
      vehicle_make_model: form.vehicle_make_model,
      vehicle_pet_features: form.vehicle_pet_features,
      vehicle_year: form.vehicle_year ? Number(form.vehicle_year) : null,
    });
    setProfile(nextProfile);
    setForm(formState(nextProfile));
    setEditingSection(null);
  }

  async function uploadAvatar(file: File) {
    const asset = await carrierCabinetApi.uploadProfileImage(file, 'avatar');
    const nextProfile = await carrierCabinetApi.updateProfile({ avatar_id: asset.id });
    setProfile(nextProfile);
    setForm(formState(nextProfile));
  }

  const [uploadingPortfolio, setUploadingPortfolio] = useState(false);
  const [uploadingVehicle, setUploadingVehicle] = useState(false);

  async function uploadPortfolio(files: File[]) {
    if (!files.length) return;
    setUploadingPortfolio(true);
    try {
      const uploadedAssets = await Promise.all(
        files.map((file) => carrierCabinetApi.uploadProfileImage(file, 'portfolio')),
      );
      setProfile((prev) => {
        if (!prev) return prev;
        const newPhotos = uploadedAssets.map((asset) => ({
          id: asset.id,
          url: asset.url,
          original_filename: asset.original_filename,
          caption: asset.caption || '',
        }));
        return {
          ...prev,
          portfolio_photos: [...newPhotos, ...prev.portfolio_photos],
          portfolio_photos_count: (prev.portfolio_photos_count ?? prev.portfolio_photos.length) + newPhotos.length,
        };
      });
      const nextProfile = await carrierCabinetApi.profile();
      setProfile(nextProfile);
      setForm(formState(nextProfile));
    } catch (error) {
      console.error('Failed to upload portfolio photos', error);
    } finally {
      setUploadingPortfolio(false);
    }
  }

  async function removePortfolioPhoto(id: number) {
    setProfile((prev) => {
      if (!prev) return prev;
      const filtered = prev.portfolio_photos.filter((p) => p.id !== id);
      return {
        ...prev,
        portfolio_photos: filtered,
        portfolio_photos_count: Math.max(0, (prev.portfolio_photos_count ?? prev.portfolio_photos.length) - 1),
      };
    });
    try {
      await deleteAsset(id);
      const nextProfile = await carrierCabinetApi.profile();
      setProfile(nextProfile);
      setForm(formState(nextProfile));
    } catch (error) {
      console.error('Failed to delete portfolio photo', error);
    }
  }

  async function handleUpdateRoutes(routes: Array<{ from_city: string; to_city: string }>) {
    const nextProfile = await carrierCabinetApi.updateProfile({ preferred_routes: routes });
    setProfile(nextProfile);
    setForm(formState(nextProfile));
  }

  async function verifyEmail() {
    setEmailState('sending');
    try {
      await carrierCabinetApi.sendEmailVerification();
      setEmailState('sent');
    } catch {
      setEmailState('error');
    }
  }

  async function handleUpdateCaption(id: number, caption: string) {
    await carrierCabinetApi.updateFileCaption(id, caption);
    const nextProfile = await carrierCabinetApi.profile();
    setProfile(nextProfile);
    setForm(formState(nextProfile));
  }

  async function uploadVehiclePhoto(files: File[]) {
    if (!files.length) return;
    setUploadingVehicle(true);
    try {
      const uploadedAssets = await Promise.all(
        files.map((file) => carrierCabinetApi.uploadProfileImage(file, 'vehicle_photo')),
      );
      setProfile((prev) => {
        if (!prev) return prev;
        const newPhotos = uploadedAssets.map((asset) => ({
          id: asset.id,
          url: asset.url,
          original_filename: asset.original_filename,
          caption: asset.caption || '',
        }));
        return {
          ...prev,
          vehicle_photos: [...newPhotos, ...(prev.vehicle_photos || [])],
        };
      });
      const nextProfile = await carrierCabinetApi.profile();
      setProfile(nextProfile);
      setForm(formState(nextProfile));
    } catch (error) {
      console.error('Failed to upload vehicle photos', error);
    } finally {
      setUploadingVehicle(false);
    }
  }

  async function removeVehiclePhoto(id: number) {
    setProfile((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        vehicle_photos: prev.vehicle_photos.filter((p) => p.id !== id),
      };
    });
    try {
      await deleteAsset(id);
      const nextProfile = await carrierCabinetApi.profile();
      setProfile(nextProfile);
      setForm(formState(nextProfile));
    } catch (error) {
      console.error('Failed to delete vehicle photo', error);
    }
  }

  const isEditing = Boolean(editingSection);
  const rootNode = isEditing ? '2867:34349' : '2867:35431';

  return (
    <CarrierCabinetLayout figmaNode={{ '1920': rootNode, '1440': rootNode }} screen="carrier-profile-internal">
      {!profile || !form ? (
        <div className="carrier-profile carrier-profile--internal" data-screen="carrier-profile-internal" data-figma-node={rootNode} />
      ) : (
        <div className={`carrier-profile carrier-profile--internal ${isEditing ? 'is-editing' : ''}`} data-screen="carrier-profile-internal" data-figma-node={rootNode}>
          <div className="carrier-profile__internal-main">
            {profile && profile.verification_status !== 'approved' && profile.verification_status !== 'pending' ? (
            <div className="carrier-profile__verification-banner">
              <div className="carrier-profile__verification-banner-text">
                <img src="/figma/assets/profile-alert-circle.svg" alt="" aria-hidden="true" />
                <div>
                  <strong>Верификация не завершена</strong>
                  <span>Загрузите документы для проверки, чтобы получить доступ к откликам на заказы</span>
                </div>
              </div>
              <Button kind="primary" size="medium" onClick={() => navigate('carrier-verify')}>
                Пройти верификацию
              </Button>
            </div>
          ) : null}
          <ProfileHero
            figmaNode={isEditing ? '2867:34355' : '2867:35436'}
            internal
            profile={profile}
            size="1920"
            onAvatarUpload={(file) => void uploadAvatar(file)}
            onPreview={() => navigate('carrier-profile-external')}
            onReviews={() => reviewsRef.current?.scrollIntoView({ behavior: 'smooth' })}
          />
          <InternalContactSection
            editing={editingSection === 'contacts'}
            emailState={emailState}
            emailVerified={Boolean(auth.user?.email_verified_at)}
            form={form}
            onCancel={cancelEdit}
            onChange={changeForm}
            onEdit={() => setEditingSection('contacts')}
            onSubmit={(event) => void save(event)}
            onVerifyEmail={() => void verifyEmail()}
            onPhoneVerified={(phone) => setProfile((current) => current ? { ...current, phone } : current)}
            profile={profile}
          />
          <InternalVehicleSection
            editing={editingSection === 'vehicle'}
            form={form}
            onCancel={cancelEdit}
            onChange={changeForm}
            onDeletePhoto={(id) => void removeVehiclePhoto(id)}
            onEdit={() => setEditingSection('vehicle')}
            onSubmit={(event) => void save(event)}
            onUploadPhoto={(files) => void uploadVehiclePhoto(files)}
            profile={profile}
            uploading={uploadingVehicle}
          />
          <InternalPortfolio
            profile={profile}
            onDelete={(id) => void removePortfolioPhoto(id)}
            onUpdateCaption={handleUpdateCaption}
            onUpload={(files) => void uploadPortfolio(files)}
            uploading={uploadingPortfolio}
          />
          <ProfileReviews internal profile={profile} reviewsRef={reviewsRef} size="1920" />
          <InternalDocuments documents={documents} profile={profile} />
          <InternalRoutes onUpdateRoutes={handleUpdateRoutes} profile={profile} />
          <AccountActions
            figmaNode={isEditing ? '2867:34564' : '2867:35623'}
            onDeleteAccount={() => {
              const confirmed = window.confirm(
                'Удалить аккаунт безвозвратно? Личные данные, файлы, рейтинг и доступ к истории будут удалены.',
              );
              if (!confirmed) return;
              void auth.deleteAccount()
                .then(() => navigate('landing'))
                .catch(() => window.alert('Не удалось удалить аккаунт. Повторите попытку или обратитесь в поддержку.'));
            }}
            onSignOut={() => void auth.signOut().then(() => navigate('landing'))}
          />
        </div>
      </div>
      )}
    </CarrierCabinetLayout>
  );
}

function InternalProfileScreen() {
  return (
    <AuthGuard allow={['carrier']}>
      <InternalProfileContent />
    </AuthGuard>
  );
}

export function CarrierProfileScreen({ screen }: { screen: CarrierProfileScreenId }) {
  return screen === 'carrier-profile-external' ? <PublicProfileScreen /> : <InternalProfileScreen />;
}
